import { Form, Select } from 'antd';
import { SizeType } from 'antd/lib/config-provider/SizeContext';
import { Rule } from 'antd/lib/form';
import { NamePath } from 'antd/lib/form/interface';
import { ColProps } from 'antd/lib/grid';
import { isFunction } from 'lodash';
import React, { ReactNode, useCallback, useContext, useRef, useState } from 'react';

import Paragraph from 'components/atoms/Paragraph';
import { Context } from 'components/hoc/Store';
import TranslateText from 'components/hoc/TranslateText';
import InfoTooltip from 'components/molecules/InfoTooltip';

import getLanguageObject from 'utils/getLanguageObject';

import TooltipComponent from './TooltipComponent';

const { Item } = Form;
const { Option } = Select;

export interface ISelectData {
    value: string | number;
    labelcz: string;
    labelen?: string;
}

interface ISelect {
    data: { value: string | number; labelcz: string; labelen?: string }[];
    placeholder?: string;
    defaultValue?: string | string[];
    disabled?: boolean;
    value?: string | string[];
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    onChange: (val: any, dataObject?: any) => void; // TODO: zbavit se any
    mode?: 'multiple' | 'tags';
    showSearch?: boolean;
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    filterOption?: (input: any, option: any) => boolean;
    // the caller may fully override the dropdown options (e.g. to highlight picked values)
    renderOptions?: (data: ISelectData[]) => React.ReactNode;
    showOptionValue?: boolean;
    optionValueFilter?: string[];
    autoComplete?: string;
    allowClear?: boolean;
    onClear?: () => void;
    showTooltipValue?: boolean;
    disableEdit?: boolean;
    size?: SizeType;
    bordered?: boolean;
    labelExtra?: any;
    id?: string;
    maxCount?: number;
    listHeight?: number;
}

interface ISelectComponent {
    data: { value: string | number; labelcz: string; labelen?: string }[];
    placeholder?: string;
    defaultValue?: string | string[];
    disabled?: boolean;
    name?: NamePath;
    labelParagraph?: string;
    value?: string | string[];
    isFormItem?: boolean;
    extramessage?: string;
    dependencies?: NamePath[];
    infoTooltipText?: string | ReactNode;
    validateTrigger?: string;
    rules?: Rule[];
    notRequired?: boolean;
    labelCol?: ColProps;
    showSearch?: boolean;
    mode?: 'multiple' | 'tags';
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    filterOption?: (input: any, option: any) => boolean;
    // the caller may fully override the dropdown options (e.g. to highlight picked values)
    renderOptions?: (data: ISelectData[]) => React.ReactNode;
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    onChange?: (val: any, dataObject?: any) => void;
    showOptionValue?: boolean;
    optionValueFilter?: string[];
    allowClear?: boolean;
    onClear?: () => void;
    showTooltipValue?: boolean;
    hide?: boolean; // ak nechceme zobrazit komponentu ale ponechat jej value
    autoComplete?: string;
    disableEdit?: boolean;
    size?: SizeType;
    bordered?: boolean;
    labelExtra?: any;
    maxCount?: number;
    listHeight?: number;
}

const PreSelect: React.FC<ISelect> = ({
    placeholder = 'VYBERTE_PLACEHOLDER',
    data,
    renderOptions,
    defaultValue,
    disabled,
    onChange,
    mode,
    value,
    showOptionValue,
    optionValueFilter,
    allowClear,
    onClear,
    showTooltipValue,
    disableEdit,
    id,
    ...rest
}) => {
    const { language, serviceMode } = useContext(Context);
    const [open, setOpen] = useState<boolean>(false);
    const selectRef = useRef<any>(null);

    /* MHO - z nějakých důvodů se nepropisovala value do PreSelect 
        (v případě LEI - matky, kdy jsem v přímé měnil hodnotu, která měla ovlivnit i finální položku)
        toto by snad mělo fungovat a ničemu nebránit */
    if (!value && defaultValue && !allowClear) value = Array.isArray(defaultValue) ? defaultValue[0] : defaultValue;

    return (
        <Select
            placeholder={<TranslateText>{placeholder}</TranslateText>}
            className="input-component"
            style={{
                width: '100%',
                borderRadius: 6
            }}
            mode={mode}
            onChange={(e: string | string[], option) => {
                onChange(e, option);
            }}
            onClick={
                // pouzite pri zmene prekladov languageEditPopover
                serviceMode && !disableEdit // eslint-disable-next-line @typescript-eslint/no-explicit-any
                    ? (e: any) => {
                          const pathElements = e.nativeEvent.composedPath() as HTMLElement[];
                          if (!open) {
                              setOpen(true);
                          }
                          if (
                              open &&
                              typeof (e.nativeEvent.target as HTMLElement)?.className === 'string' &&
                              pathElements?.filter(
                                  u =>
                                      typeof u.className === 'string' &&
                                      (u.className || '').includes('ant-select-show-arrow')
                              ).length
                          ) {
                              setOpen(false);
                              selectRef.current?.blur();
                          }
                      }
                    : () => {}
            }
            open={serviceMode && !disableEdit ? open : undefined}
            defaultValue={defaultValue}
            disabled={disabled}
            value={value}
            allowClear={allowClear}
            onClear={onClear}
            autoComplete="dontshow"
            {...rest}
            ref={selectRef}
            id={id}
            data-testid={id}
        >
            {renderOptions
                ? renderOptions(data)
                : data.map(({ value: selectValue, labelcz, labelen }, i) =>
                      showOptionValue ? (
                          <Option className="select-option-component" value={selectValue} key={i}>
                              <TranslateText
                                  id={`${id || ''}_${selectValue}`}
                                  setSelectOpen={setOpen}
                                  disableEdit={disableEdit}
                              >
                                  {`${
                                      optionValueFilter && selectValue
                                          ? optionValueFilter.includes(String(selectValue))
                                              ? ''
                                              : selectValue + ' - '
                                          : selectValue + ' - '
                                  }${
                                      getLanguageObject(language)[language === 'en' && labelen ? labelen : labelcz] ||
                                      (language === 'en' && labelen ? labelen : labelcz)
                                  }`}
                              </TranslateText>
                          </Option>
                      ) : showTooltipValue ? (
                          <Option className="select-option-component" value={selectValue} key={i}>
                              <TooltipComponent title={language === 'en' && labelen ? labelen : labelcz}>
                                  {/* medzera pod tymto komentom je potrebna inak nefunguje tooltip */}
                                  {
                                      <TranslateText
                                          id={`${id || ''}_${selectValue}`}
                                          setSelectOpen={setOpen}
                                          disableEdit={disableEdit}
                                      >
                                          {language === 'en' && labelen ? labelen : labelcz}
                                      </TranslateText>
                                  }
                              </TooltipComponent>
                          </Option>
                      ) : (
                          <Option className="select-option-component" value={selectValue} key={i}>
                              {
                                  <TranslateText
                                      id={`${id || ''}_${selectValue}`}
                                      setSelectOpen={setOpen}
                                      disableEdit={disableEdit}
                                  >
                                      {language === 'en' && labelen ? labelen : labelcz}
                                  </TranslateText>
                              }
                          </Option>
                      )
                  )}
        </Select>
    );
};

/**
 * SelectComponenta je rozdelena podle labelParagraph, ne vzdycky je potreba mit label a tim padem i Item
 * @param props ISelectComponent
 */
const SelectComponent: React.FC<ISelectComponent> = props => {
    const { language } = useContext(Context);
    const {
        labelParagraph,
        infoTooltipText = '',
        isFormItem,
        name,
        value,
        placeholder = 'VYBERTE_PLACEHOLDER',
        defaultValue,
        extramessage = '',
        data = [],
        dependencies = [],
        validateTrigger,
        rules = [
            {
                required: true,
                message: <TranslateText>NEVALIDNI_POVINNA_POLOZKA</TranslateText>
            }
        ],
        notRequired,
        showSearch = true,
        disabled = false,
        onChange,
        mode,
        filterOption,
        renderOptions,
        showOptionValue,
        optionValueFilter,
        allowClear = false,
        onClear,
        showTooltipValue,
        hide,
        disableEdit,
        size,
        bordered,
        labelExtra,
        maxCount,
        listHeight
    } = props;

    const updatedValue = useCallback(
        (val: string | [string], option: any) => {
            if (isFunction(onChange)) onChange(val, option);
        },
        [onChange]
    );

    if (isFormItem)
        return (
            <div
                style={
                    hide
                        ? {
                              display: 'none'
                          }
                        : {}
                }
            >
                <Item
                    label={
                        labelParagraph ? (
                            <>
                                <Paragraph text={labelParagraph} strong={true} disableEdit={disableEdit} />
                                {infoTooltipText && <InfoTooltip title={infoTooltipText} />}
                                {labelExtra && labelExtra}
                            </>
                        ) : null
                    }
                    name={name}
                    dependencies={dependencies}
                    rules={notRequired ? undefined : rules}
                    validateTrigger={validateTrigger}
                    initialValue={defaultValue} // pri nacitani dat zajisti ze sa setnu aj do form
                    id={name}
                    data-testid={name}
                    extra={
                        <Paragraph
                            text={extramessage}
                            className="extramessage-text"
                            style={{
                                display: 'block'
                            }}
                        />
                    }
                >
                    <PreSelect
                        showSearch={showSearch}
                        allowClear={allowClear || notRequired}
                        onClear={onClear}
                        value={value}
                        mode={mode}
                        data={data}
                        showOptionValue={showOptionValue}
                        optionValueFilter={optionValueFilter}
                        placeholder={placeholder}
                        defaultValue={defaultValue}
                        onChange={updatedValue}
                        disableEdit={disableEdit}
                        disabled={disabled}
                        listHeight={listHeight}
                        maxCount={maxCount}
                        filterOption={(input, option) => {
                            const getTranslatedVal = (label: string) => {
                                const translatedText: string = getLanguageObject(language)[label];
                                return translatedText || label;
                            };
                            return (
                                option.value.toLowerCase().indexOf(input.toLowerCase()) === 0 ||
                                (data && language === 'en' && data[option.key].labelen
                                    ? getTranslatedVal(data[option?.key].labelen || '')
                                          .toLowerCase()
                                          .indexOf(input.toLowerCase()) === 0
                                    : getTranslatedVal(data[option?.key].labelcz)
                                          .toLowerCase()
                                          .indexOf(input.toLowerCase()) === 0)
                            );
                        }}
                        showTooltipValue={showTooltipValue}
                        id={name}
                        renderOptions={renderOptions}
                    />
                </Item>
            </div>
        );

    return (
        <PreSelect
            showSearch={showSearch}
            mode={mode}
            data={data}
            showOptionValue={showOptionValue}
            optionValueFilter={optionValueFilter}
            placeholder={placeholder}
            value={value}
            defaultValue={defaultValue}
            disableEdit={disableEdit}
            onChange={updatedValue}
            disabled={disabled}
            // a caller may override the option filtering (e.g. match by the translated label)
            filterOption={
                filterOption ??
                ((input, option) =>
                    (typeof option.children.props.children === 'string'
                        ? option.children.props.children
                        : option.children.props.children[0]
                    )
                        .toLowerCase()
                        .indexOf(input.toLowerCase()) === 0)
            }
            allowClear={allowClear}
            onClear={onClear}
            showTooltipValue={showTooltipValue}
            size={size}
            maxCount={maxCount}
            bordered={bordered}
            listHeight={listHeight}
            renderOptions={renderOptions}
        />
    );
};

export default SelectComponent;
